import React, { createContext, useContext, useEffect, useState } from 'react';
import { getConfig } from '../config';

interface User {
  id: string | number;
  email: string;
  isAdmin?: boolean;
  isSuperAdmin: boolean;
  firstName?: string;
  lastName?: string;
}

interface AuthContextType {
  user: User | null;
  isSuperadmin: boolean;
  setUserForSession: (user: User) => void;
  logout: () => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const isSuperadmin = Boolean(user?.isSuperAdmin);
  const { apiBase } = getConfig();

  const setUserForSession = (u: User) => {
    setUser(u);
    localStorage.setItem('mc_user', JSON.stringify(u));
  };

  const clearSession = () => {
    setUser(null);
    localStorage.removeItem('mc_user');
  };

  const logout = async () => {
    try {
      await fetch(`${apiBase}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      // ignore network errors
    } finally {
      clearSession();
    }
  };

  const normalizeUser = (raw: any): User | null => {
    if (!raw || typeof raw !== 'object') return null;
    const id = raw.id ?? raw.sub ?? raw.userId;
    const email = raw.email;
    if ((typeof id !== 'number' && typeof id !== 'string') || typeof email !== 'string') {
      return null;
    }

    return {
      id,
      email,
      firstName: raw.firstName,
      lastName: raw.lastName,
      isAdmin: Boolean(raw.isAdmin ?? raw.is_admin ?? false),
      isSuperAdmin: Boolean(
        raw.isSuperAdmin ??
        raw.is_super_admin ??
        false,
      ),
    };
  };

  useEffect(() => {
    const stored = localStorage.getItem('mc_user');
    let candidate: User | null = null;

    if (stored) {
      try {
        candidate = normalizeUser(JSON.parse(stored));
      } catch {
        candidate = null;
      }
    }

    (async () => {
      try {
        if (candidate) {
          // Optimistically show stored user to avoid redirect flicker on hard refresh
          setUser(candidate);
        }
        const res = await fetch(`${apiBase}/auth/profile`, {
          method: 'GET',
          credentials: 'include',
        });
        if (!res.ok) throw new Error('Not authenticated');
        const profile = await res.json();
        const nextUser = normalizeUser(profile?.user ?? profile) ?? candidate;
        if (!nextUser) throw new Error('No user');
        if (!nextUser.isSuperAdmin) throw new Error('Not superadmin');
        setUser(nextUser);
      } catch {
        clearSession();
      } finally {
        setLoading(false);
      }
    })();
  }, [apiBase]);

  return (
    <AuthContext.Provider value={{ user, isSuperadmin, setUserForSession, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
